export type JoinState = 'soon' | 'live';

const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * Determines the join state for an event based on its start/end times.
 * - 'soon': within 1 hour before start
 * - 'live': between start and end (defaults to start + 1h if no end)
 * - null: outside the window
 */
export const getJoinState = (
  startIso?: string | Date | null,
  endIso?: string | Date | null,
): JoinState | null => {
  if (!startIso) return null;
  const startMs = new Date(startIso).getTime();
  if (!Number.isFinite(startMs)) return null;
  const nowMs = Date.now();
  const endMs = endIso ? new Date(endIso).getTime() : startMs + ONE_HOUR_MS;
  if (nowMs > endMs) return null;
  if (nowMs < startMs - ONE_HOUR_MS) return null;
  if (nowMs >= startMs) return 'live';
  return 'soon';
};

/**
 * Extracts the first URL from a string (e.g. event location field).
 * Handles both full URLs and bare meet.google.com/xxx references.
 */
export const extractFirstUrl = (raw?: string | null): string | null => {
  if (!raw) return null;
  const match = raw.match(/https?:\/\/[^\s)]+/i);
  if (match?.[0]) return match[0];
  const meetMatch = raw.match(/(?:^|\s)(meet\.google\.com\/[^\s)]+)/i);
  if (meetMatch?.[1]) return `https://${meetMatch[1]}`;
  return null;
};

/**
 * Strips URLs from a location string, returning the remaining text or null.
 */
export const stripUrlFromLocation = (raw?: string | null): string | null => {
  if (!raw) return null;
  const cleaned = raw
    .replace(/https?:\/\/[^\s)]+/gi, '')
    .replace(/meet\.google\.com\/[^\s)]+/gi, '')
    .trim();
  return cleaned || null;
};
